import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { slugify } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Copy,
  Eye,
  FileCheck,
  FilePlus,
  FileSearch,
  FileText,
  Files,
  Folder,
  Handshake,
  Link2,
  Link as LinkIcon,
  Loader2,
  Lock,
  RefreshCw,
  Scale,
  Settings2,
  Shield,
  Sparkles,
  UserCheck,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import {
  useUpdateTemplate,
  useGeneratePreviewToken,
  useRevokePreviewToken,
} from '@/modules/templates/hooks/use-templates';
import { checkSlugAvailability } from '@/modules/templates/services/templates.service';
import { TEMPLATE_CATEGORIES } from '@/modules/templates/constants/template-categories';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { TFunction } from 'i18next';

const iconOptions = [
  { value: 'FileText', label: 'File Text', icon: FileText },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'Lock', label: 'Lock', icon: Lock },
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'Sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'FileCheck', label: 'File Check', icon: FileCheck },
  { value: 'FilePlus', label: 'File Plus', icon: FilePlus },
  { value: 'FileSearch', label: 'File Search', icon: FileSearch },
  { value: 'Files', label: 'Files', icon: Files },
  { value: 'Folder', label: 'Folder', icon: Folder },
  { value: 'Scale', label: 'Scale', icon: Scale },
  { value: 'Briefcase', label: 'Briefcase', icon: Briefcase },
  { value: 'Building', label: 'Building', icon: Building },
  { value: 'Handshake', label: 'Handshake', icon: Handshake },
  { value: 'UserCheck', label: 'User Check', icon: UserCheck },
  { value: 'ClipboardList', label: 'Clipboard List', icon: ClipboardList },
] as const;

const getFormSchema = (t: TFunction) =>
  z.object({
    slug: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_SLUGREQUIRED'))
      .max(100, t('ORGTEMPLATES_VALIDATION_SLUGMAX'))
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t('ORGTEMPLATES_VALIDATION_SLUGFORMAT')),
    title: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_TITLEREQUIRED'))
      .max(200, t('ORGTEMPLATES_VALIDATION_TITLEMAX')),
    description: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONREQUIRED'))
      .max(1000, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONMAX')),
    icon: z.string().default('FileText'),
    category: z.string().optional().nullable(),
    available: z.boolean().default(false),
    keywords: z.array(z.string()).default([]).optional(),
    estimatedMinutes: z.number().int().positive().optional().nullable(),
    systemPromptRole: z.string().optional().nullable(),
    systemPrompt: z.string().optional().nullable(),
  });

interface FormData {
  slug: string;
  title: string;
  description: string;
  icon: string;
  category?: string | null;
  available: boolean;
  keywords: string[];
  estimatedMinutes?: number | null;
  systemPromptRole?: string | null;
  systemPrompt?: string | null;
}

interface EditOrgTemplateFormProps {
  orgSlug: string;
  orgId: string;
  template: TemplateWithScreens;
  previewToken?: string | null;
}

export function EditOrgTemplateForm({
  orgSlug,
  template,
  previewToken: initialPreviewToken,
}: EditOrgTemplateFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const formSchema = useMemo(() => getFormSchema(t), [t]);
  const updateTemplateMutation = useUpdateTemplate();
  const generateTokenMutation = useGeneratePreviewToken();
  const revokeTokenMutation = useRevokePreviewToken();

  const [keywordInput, setKeywordInput] = useState('');
  const [aiConfigExpanded, setAiConfigExpanded] = useState(false);
  const [previewToken, setPreviewToken] = useState<string | null>(initialPreviewToken || null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(true);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const slugCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      slug: template.slug,
      title: template.title,
      description: template.description,
      icon: template.icon,
      category: template.category || null,
      available: template.available,
      keywords: Array.isArray(template.keywords) ? template.keywords : [],
      estimatedMinutes: template.estimatedMinutes,
      systemPromptRole: template.systemPromptRole,
      systemPrompt: template.systemPrompt,
    },
  });

  const checkSlug = useCallback(
    async (slug: string) => {
      if (!slug || slug.length < 2 || slug === template.slug) {
        setSlugAvailable(null);
        return;
      }
      setIsCheckingSlug(true);
      try {
        const available = await checkSlugAvailability(slug, template.id);
        setSlugAvailable(available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setIsCheckingSlug(false);
      }
    },
    [template.id, template.slug]
  );

  const debouncedSlugCheck = useCallback(
    (slug: string) => {
      if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
      slugCheckTimeout.current = setTimeout(() => checkSlug(slug), 300);
    },
    [checkSlug]
  );

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    form.setValue('title', title);
    if (!slugManuallyEdited) {
      const newSlug = slugify(title);
      form.setValue('slug', newSlug);
      debouncedSlugCheck(newSlug);
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    form.setValue('slug', slug);
    setSlugManuallyEdited(true);
    debouncedSlugCheck(slug);
  };

  const handleResetSlug = () => {
    const title = form.getValues('title');
    const newSlug = slugify(title);
    form.setValue('slug', newSlug);
    setSlugManuallyEdited(false);
    debouncedSlugCheck(newSlug);
  };

  const handleSubmit = async (data: FormData) => {
    try {
      await updateTemplateMutation.mutateAsync({
        itemId: template.id,
        input: {
          Slug: data.slug,
          Title: data.title,
          Keywords: data.keywords,
          Description: data.description,
          Icon: data.icon,
          Category: data.category ?? undefined,
          Available: data.available,
          Href: `/templates/${data.slug}/generate`,
          EstimatedMinutes: data.estimatedMinutes ?? undefined,
          SystemPromptRole: data.systemPromptRole ?? undefined,
          SystemPrompt: data.systemPrompt ?? undefined,
        },
      });
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_TOAST_UPDATESUCCESS') });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: error instanceof Error ? error.message : t('ORGTEMPLATES_EDIT_TOAST_UPDATEERROR'),
      });
    }
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim().toLowerCase();
    const currentKeywords = form.getValues('keywords') ?? [];
    if (keyword && !currentKeywords.includes(keyword)) {
      form.setValue('keywords', [...currentKeywords, keyword]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    const currentKeywords = form.getValues('keywords') ?? [];
    form.setValue(
      'keywords',
      currentKeywords.filter((k) => k !== keyword)
    );
  };

  const handleGeneratePreviewToken = async () => {
    try {
      const token = await generateTokenMutation.mutateAsync(template.id);
      setPreviewToken(token);
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_GENERATED') });
    } catch {
      // handled by mutation
    }
  };

  const handleRevokePreviewToken = async () => {
    try {
      await revokeTokenMutation.mutateAsync(template.id);
      setPreviewToken(null);
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_REVOKED') });
    } catch {
      // handled by mutation
    }
  };

  const handleCopyPreviewLink = () => {
    if (previewToken) {
      const previewUrl = `${window.location.origin}/templates/${form.watch('slug') || template.slug}/generate?preview=${previewToken}`;
      navigator.clipboard.writeText(previewUrl);
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_COPIED') });
    }
  };

  const selectedIcon =
    iconOptions.find((opt) => opt.value === form.watch('icon'))?.icon || FileText;
  const IconComponent = selectedIcon;

  const totalScreens = template.screens.length;
  const totalFields = template.screens.reduce((acc, screen) => acc + screen.fields.length, 0);

  return (
    <div className="w-full md:px-4 md:py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 md:mb-8">
        <div className="flex items-center gap-4">
          <Link to={`/org/${orgSlug}/templates`}>
            <Button variant="ghost" size="icon" className="bg-brand-surface">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-lg md:text-2xl font-bold text-[hsl(var(--fg))] font-heading">
                <span>{t('ORGTEMPLATES_EDIT_HEADER_TITLE')}</span>
              </h1>
              <p className="text-sm text-globe-grey">{template.title}</p>
            </div>
          </div>
        </div>
        <Badge variant={template.available ? 'success' : 'secondary'} className="gap-1">
          {template.available ? (
            <>{t('ORGTEMPLATES_EDIT_STATUS_ACTIVE')}</>
          ) : (
            <>{t('ORGTEMPLATES_EDIT_STATUS_DRAFT')}</>
          )}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-6 ">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="details">
            <Settings2 className="h-4 w-4 mr-2" />
            {t('ORGTEMPLATES_EDIT_TABS_DETAILS')}
          </TabsTrigger>
          <TabsTrigger value="form-builder">
            <FileText className="h-4 w-4 mr-2" />
            {t('ORGTEMPLATES_EDIT_TABS_FORMBUILDER')}
            {totalScreens > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalScreens}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Template Details Tab */}
        <TabsContent value="details">
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('ORGTEMPLATES_FORM_TITLE')}</CardTitle>
                <CardDescription>{t('ORGTEMPLATES_FORM_DESCRIPTION')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Title and Slug */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('ORGTEMPLATES_FORM_TITLELABEL')}</Label>
                    <Input
                      id="title"
                      placeholder={t('ORGTEMPLATES_FORM_TITLEPLACEHOLDER')}
                      value={form.watch('title')}
                      onChange={handleTitleChange}
                    />
                    {form.formState.errors.title && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.title.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="slug">{t('ORGTEMPLATES_FORM_SLUGLABEL')}</Label>
                      {slugManuallyEdited && (
                        <button
                          type="button"
                          onClick={handleResetSlug}
                          className="flex items-center gap-1 text-xs text-selise-blue hover:underline"
                        >
                          <Link2 className="size-3" />
                          {t('ORGTEMPLATES_FORM_SLUGAUTOGENERATE')}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        id="slug"
                        placeholder={t('ORGTEMPLATES_FORM_SLUGPLACEHOLDER')}
                        value={form.watch('slug')}
                        onChange={handleSlugChange}
                        className={
                          slugAvailable === false
                            ? 'border-destructive pr-10'
                            : slugAvailable === true
                              ? 'border-lime-green pr-10'
                              : 'pr-10'
                        }
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {isCheckingSlug ? (
                          <Loader2 className="size-4 animate-spin text-globe-grey" />
                        ) : slugAvailable === true ? (
                          <Check className="size-4 text-lime-green" />
                        ) : slugAvailable === false ? (
                          <AlertCircle className="size-4 text-destructive" />
                        ) : null}
                      </div>
                    </div>
                    {form.formState.errors.slug && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.slug.message}
                      </p>
                    )}
                    {slugAvailable === false && (
                      <p className="text-sm text-destructive">{t('ORGTEMPLATES_FORM_SLUGTAKEN')}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">{t('ORGTEMPLATES_FORM_DESCRIPTIONLABEL')}</Label>
                  <textarea
                    id="description"
                    placeholder={t('ORGTEMPLATES_FORM_DESCRIPTIONPLACEHOLDER')}
                    className="flex min-h-24 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...form.register('description')}
                  />
                  {form.formState.errors.description && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.description.message}
                    </p>
                  )}
                </div>

                {/* Icon */}
                <div className="space-y-2">
                  <Label>{t('ORGTEMPLATES_FORM_ICONLABEL')}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-muted">
                      <IconComponent className="size-5 text-selise-blue" />
                    </div>
                    <select
                      className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={form.watch('icon')}
                      onChange={(e) => form.setValue('icon', e.target.value)}
                    >
                      {iconOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(`ORGTEMPLATES_ICONS_${option.value.toUpperCase()}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={form.watch('category') ?? ''}
                    onChange={(e) => form.setValue('category', e.target.value || null)}
                  >
                    <option value="">Select a category</option>
                    {TEMPLATE_CATEGORIES.map((cat) => (
                      <option key={cat.slug} value={cat.slug}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* URL Preview */}
                {form.watch('slug') && (
                  <div className="space-y-2">
                    <Label>{t('ORGTEMPLATES_FORM_URLPREVIEWLABEL')}</Label>
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-muted">
                      <code className="text-sm text-globe-grey">
                        /templates/{form.watch('slug')}/generate
                      </code>
                    </div>
                    <p className="text-xs text-globe-grey">
                      {t('ORGTEMPLATES_FORM_URLPREVIEWHELP')}
                    </p>
                  </div>
                )}

                {/* Available toggle */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                      checked={form.watch('available')}
                      onChange={(e) => form.setValue('available', e.target.checked)}
                    />
                    <span className="text-sm font-medium text-[hsl(var(--fg))]">
                      {t('ORGTEMPLATES_FORM_AVAILABLELABEL')}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-globe-grey -mt-4">
                  {t('ORGTEMPLATES_FORM_AVAILABLEHELP')}
                </p>

                {/* Keywords */}
                <div className="space-y-2">
                  <Label>{t('ORGTEMPLATES_FORM_KEYWORDSLABEL')}</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('ORGTEMPLATES_FORM_KEYWORDSPLACEHOLDER')}
                      className="flex-1 "
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addKeyword();
                        }
                      }}
                    />
                    <Button type="button" variant="primary" onClick={addKeyword}>
                      {t('ORGTEMPLATES_FORM_KEYWORDSADD')}
                    </Button>
                  </div>
                  {(form.watch('keywords') ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(form.watch('keywords') || []).map((keyword) => (
                        <Badge key={keyword} variant="secondary" className="gap-1 pr-1">
                          {keyword}
                          <button
                            type="button"
                            onClick={() => removeKeyword(keyword)}
                            className="ml-1 rounded-full hover:bg-destructive/20"
                          >
                            <X className="size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Estimated Minutes */}
                <div className="space-y-2">
                  <Label htmlFor="estimatedMinutes">
                    {t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESLABEL')}
                  </Label>
                  <Input
                    id="estimatedMinutes"
                    type="number"
                    min="1"
                    placeholder={t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESPLACEHOLDER')}
                    {...form.register('estimatedMinutes', {
                      setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                    })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Internal Preview Link Card */}
            {!form.watch('available') && (
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-selise-blue" />
                    <CardTitle>{t('ORGTEMPLATES_EDIT_PREVIEW_TITLE')}</CardTitle>
                  </div>
                  <CardDescription>{t('ORGTEMPLATES_EDIT_PREVIEW_DESCRIPTION')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {previewToken ? (
                    <>
                      <div className="space-y-2">
                        <Label>{t('ORGTEMPLATES_EDIT_PREVIEW_URLLABEL')}</Label>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/templates/${form.watch('slug') || template.slug}/generate?preview=${previewToken}`}
                            className="font-mono text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleCopyPreviewLink}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-globe-grey">
                          {t('ORGTEMPLATES_EDIT_PREVIEW_URLHELP')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGeneratePreviewToken}
                          disabled={generateTokenMutation.isPending}
                          className="gap-2"
                        >
                          {generateTokenMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          {t('ORGTEMPLATES_EDIT_PREVIEW_REGENERATE')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleRevokePreviewToken}
                          disabled={revokeTokenMutation.isPending}
                          className="gap-2 text-destructive hover:text-destructive"
                        >
                          {revokeTokenMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                          {t('ORGTEMPLATES_EDIT_PREVIEW_REVOKE')}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-selise-blue/5 border border-selise-blue/20 rounded-lg">
                        <div className="flex items-start gap-3">
                          <LinkIcon className="h-5 w-5 text-selise-blue mt-0.5" />
                          <div>
                            <p className="font-medium text-[hsl(var(--fg))]">
                              {t('ORGTEMPLATES_EDIT_PREVIEW_EMPTYTITLE')}
                            </p>
                            <p className="text-sm text-globe-grey mt-1">
                              {t('ORGTEMPLATES_EDIT_PREVIEW_EMPTYDESCRIPTION')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handleGeneratePreviewToken}
                        disabled={generateTokenMutation.isPending}
                        className="gap-2"
                      >
                        {generateTokenMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <LinkIcon className="h-4 w-4" />
                        )}
                        {t('ORGTEMPLATES_EDIT_PREVIEW_GENERATE')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI Configuration Card */}
            <Card className="mb-6">
              <CardHeader
                className="cursor-pointer"
                onClick={() => setAiConfigExpanded(!aiConfigExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wand2 className="h-5 w-5 text-selise-blue" />
                    <CardTitle>{t('ORGTEMPLATES_EDIT_AI_TITLE')}</CardTitle>
                    {(form.watch('systemPrompt') || form.watch('systemPromptRole')) && (
                      <Badge variant="secondary" className="text-xs">
                        {t('ORGTEMPLATES_EDIT_AI_CUSTOMBADGE')}
                      </Badge>
                    )}
                  </div>
                  {aiConfigExpanded ? (
                    <ChevronUp className="h-5 w-5 text-globe-grey" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-globe-grey" />
                  )}
                </div>
                <CardDescription>{t('ORGTEMPLATES_EDIT_AI_DESCRIPTION')}</CardDescription>
              </CardHeader>
              {aiConfigExpanded && (
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="systemPromptRole">{t('ORGTEMPLATES_EDIT_AI_ROLELABEL')}</Label>
                    <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_EDIT_AI_ROLEHELP')}</p>
                    <Input
                      id="systemPromptRole"
                      placeholder={t('ORGTEMPLATES_EDIT_AI_ROLEPLACEHOLDER')}
                      {...form.register('systemPromptRole')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="systemPrompt">{t('ORGTEMPLATES_EDIT_AI_PROMPTLABEL')}</Label>
                    <p className="text-sm text-globe-grey">
                      {t('ORGTEMPLATES_EDIT_AI_PROMPTHELP')}
                    </p>
                    <textarea
                      id="systemPrompt"
                      placeholder={t('ORGTEMPLATES_EDIT_AI_PROMPTPLACEHOLDER')}
                      className="flex min-h-64 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      {...form.register('systemPrompt')}
                    />
                    {form.watch('systemPrompt') && (
                      <div className="flex justify-between items-center text-sm text-globe-grey">
                        <span>
                          {t('ORGTEMPLATES_EDIT_AI_CHARACTERS', {
                            count: form.watch('systemPrompt')?.length || 0,
                          })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => form.setValue('systemPrompt', null)}
                          className="h-6 text-sm text-destructive hover:text-destructive"
                        >
                          {t('ORGTEMPLATES_EDIT_AI_CLEAR')}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4">
              <Button type="button" size={'sm'} variant="primaryOutline" asChild>
                <Link to={`/org/${orgSlug}/templates`}>{t('ORGTEMPLATES_ACTIONS_CANCEL')}</Link>
              </Button>
              <Button
                type="submit"
                size={'sm'}
                disabled={updateTemplateMutation.isPending}
                variant="primary"
              >
                {updateTemplateMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t('ORGTEMPLATES_EDIT_ACTIONS_SUBMIT')}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Form Builder Tab */}
        <TabsContent value="form-builder">
          <Card>
            <CardContent className="py-12">
              <div className="text-center space-y-4 max-w-md mx-auto">
                <div className="h-16 w-16 mx-auto rounded-full bg-selise-blue/10 flex items-center justify-center">
                  <FileText className="h-8 w-8 text-selise-blue" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-[hsl(var(--fg))]">
                    {t('ORGTEMPLATES_EDIT_FORMBUILDER_TITLE')}
                  </h3>
                  <p className="text-sm text-globe-grey">
                    {totalScreens === 0
                      ? t('ORGTEMPLATES_EDIT_FORMBUILDER_EMPTY')
                      : t('ORGTEMPLATES_EDIT_FORMBUILDER_SUMMARY', {
                          screens: totalScreens,
                          fields: totalFields,
                        })}
                  </p>
                </div>

                {template.screens.length > 0 && (
                  <div className="text-left mt-6 space-y-2">
                    {template.screens.map((screen, index) => (
                      <div
                        key={screen.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-globe-grey">{index + 1}.</span>
                          <span className="font-medium text-[hsl(var(--fg))]">{screen.title}</span>
                        </div>
                        <Badge variant="secondary">
                          {t('ORGTEMPLATES_EDIT_FORMBUILDER_SCREENFIELDS', {
                            count: screen.fields.length,
                          })}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-4">
                  <Button
                    size={'sm'}
                    variant="primary"
                    onClick={() => navigate(`/org/${orgSlug}/templates/${template.id}/builder`)}
                    className="gap-2"
                  >
                    {t('ORGTEMPLATES_EDIT_FORMBUILDER_OPEN')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
